import api from '../utils/axiosConfig';
import { AISuggestion } from '../types';

export interface ResolvedTicketMetric {
  id: string;
  title: string;
  priority: string | null;
  created_at: string;
  resolved_at: string;
  mttr_minutes: number;
}

export interface TicketMetrics {
  total_tickets: number;
  by_status: Record<string, number>;
  resolved_count: number;
  avg_mttr_minutes: number | null;
  resolved_tickets: ResolvedTicketMetric[];
  benchmark: {
    manual_avg_minutes: number;
    soar_avg_minutes: number;
    source: string;
  };
}

export interface OverviewTicket {
  id: string;
  title: string;
  priority: string;
  status: string;
  created_at: string | null;
  pending_suggestions?: number;
}

export interface OverviewData {
  by_priority: Record<string, number>;
  by_status: Record<string, number>;
  sla_breach_count: number;
  unassigned: OverviewTicket[];
  my_tickets: OverviewTicket[];
  tickets_in_review: OverviewTicket[];
  top_categories: { category: string; count: number }[];
  top_clients: { name: string; ticket_count: number }[];
  alert_count_24h: number;
  alert_trend_12h: number[];
  avg_mttr_minutes: number | null;
  benchmark: { manual_avg_minutes: number; soar_avg_minutes: number; source: string };
}

export class TicketsService {
  static async getOverview(clientId?: string): Promise<OverviewData> {
    try {
      const response = await api.get('/tickets/overview', {
        params: clientId ? { client_id: clientId } : undefined,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch overview data');
    }
  }

  static async getMetrics(clientId?: string): Promise<TicketMetrics> {
    try {
      const response = await api.get('/tickets/metrics', {
        params: clientId ? { client_id: clientId } : undefined,
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching ticket metrics:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch ticket metrics');
    }
  }

  static async getAISuggestions(
    ticketId: string,
    status?: string,
  ): Promise<{ suggestions: AISuggestion[]; count: number }> {
    try {
      const response = await api.get(`/tickets/${ticketId}/ai-suggestions`, {
        params: status ? { status } : {},
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch AI suggestions');
    }
  }

  static async acceptAISuggestion(ticketId: string, suggestionId: string): Promise<AISuggestion> {
    try {
      const response = await api.post(`/tickets/${ticketId}/ai-suggestions/${suggestionId}/accept`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to accept suggestion');
    }
  }

  static async rejectAISuggestion(ticketId: string, suggestionId: string): Promise<AISuggestion> {
    try {
      const response = await api.post(`/tickets/${ticketId}/ai-suggestions/${suggestionId}/reject`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to reject suggestion');
    }
  }

  static async editAndAcceptAISuggestion(
    ticketId: string,
    suggestionId: string,
    value: any,
  ): Promise<AISuggestion> {
    try {
      const response = await api.post(
        `/tickets/${ticketId}/ai-suggestions/${suggestionId}/edit-and-accept`,
        { edited_content: value },
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to edit and accept suggestion');
    }
  }
}
